import { ReactNode, useEffect, useRef, useState } from 'react';
import { useSession } from '@clerk/clerk-react';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import VerificationCodeInput from '../ui/VerificationCodeInput';

export type ReverificationPurpose = 'add-work-email' | 'remove-email' | 'make-primary';

type EmailCodeFactor = {
  strategy: 'email_code';
  emailAddressId: string;
  safeIdentifier?: string | null;
};

type SessionVerificationResource = {
  status: string;
  supportedFirstFactors?: Array<{
    strategy: string;
    emailAddressId?: string;
    safeIdentifier?: string | null;
  }>;
};

type VerifiableSession = {
  startVerification: (params: { level?: string }) => Promise<SessionVerificationResource>;
  prepareFirstFactorVerification: (params: {
    strategy: 'email_code';
    emailAddressId: string;
  }) => Promise<unknown>;
  attemptFirstFactorVerification: (params: {
    strategy: 'email_code';
    code: string;
  }) => Promise<unknown>;
};

function clerkErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'errors' in error) {
    const errors = (error as { errors?: Array<{ longMessage?: string; message?: string }> }).errors;
    const first = errors?.[0];
    if (first?.longMessage) return first.longMessage;
    if (first?.message) return first.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong. Please try again.';
}

function UnlinkedEmail({ address }: { address: string }) {
  const [local, domain] = address.split('@');
  if (!domain) {
    return <span className="font-medium text-gray-900">{address}</span>;
  }
  return (
    <span className="font-medium text-gray-900 whitespace-nowrap">
      {local}
      <span aria-hidden="true">@</span>
      <span className="sr-only"> at </span>
      {domain}
    </span>
  );
}

function pickEmailCodeFactor(
  factors: SessionVerificationResource['supportedFirstFactors'],
  preferredEmailAddressId?: string | null
): EmailCodeFactor | null {
  const emailFactors = (factors || []).filter(
    (item): item is EmailCodeFactor => item.strategy === 'email_code' && Boolean(item.emailAddressId)
  );
  if (preferredEmailAddressId) {
    const preferred = emailFactors.find((item) => item.emailAddressId === preferredEmailAddressId);
    if (preferred) return preferred;
  }
  return emailFactors[0] ?? null;
}

function confirmationMessage(purpose: ReverificationPurpose, email: ReactNode): ReactNode {
  if (purpose === 'add-work-email') {
    return <>Enter the code we sent to {email} to add a new work email to your account.</>;
  }
  if (purpose === 'remove-email') {
    return <>Enter the code we sent to {email} to remove an email from your account.</>;
  }
  if (purpose === 'make-primary') {
    return <>Enter the code we sent to {email} to change your primary email.</>;
  }
  return <>Enter the code we sent to {email} to confirm this change.</>;
}

type AccountReverificationModalProps = {
  isOpen: boolean;
  level?: string;
  purpose?: ReverificationPurpose;
  preferredEmailAddressId?: string | null;
  onComplete: () => void;
  onCancel: () => void;
};

export default function AccountReverificationModal({
  isOpen,
  level = 'first_factor',
  purpose = 'add-work-email',
  preferredEmailAddressId,
  onComplete,
  onCancel,
}: AccountReverificationModalProps) {
  const { session } = useSession();
  const verificationSession = session as unknown as VerifiableSession | null | undefined;
  const startedRef = useRef(false);
  const cancelledRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const [factor, setFactor] = useState<EmailCodeFactor | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      startedRef.current = false;
      cancelledRef.current = true;
      setFactor(null);
      setCode('');
      setError(null);
      setBusy(false);
      setStarting(false);
      return;
    }
    if (!verificationSession || startedRef.current) return;
    startedRef.current = true;
    cancelledRef.current = false;
    setStarting(true);
    void verificationSession
      .startVerification({ level })
      .then(async (response) => {
        if (cancelledRef.current) return;
        if (response.status === 'complete') {
          onCompleteRef.current();
          return;
        }
        if (response.status !== 'needs_first_factor') {
          setError('Could not start account verification. Try again.');
          return;
        }
        const emailFactor = pickEmailCodeFactor(
          response.supportedFirstFactors,
          preferredEmailAddressId
        );
        if (!emailFactor) {
          setError('Email verification is not available on this account.');
          return;
        }
        if (cancelledRef.current) return;
        await verificationSession.prepareFirstFactorVerification({
          strategy: 'email_code',
          emailAddressId: emailFactor.emailAddressId,
        });
        if (cancelledRef.current) return;
        setFactor(emailFactor);
      })
      .catch((err) => {
        if (cancelledRef.current) return;
        setError(clerkErrorMessage(err));
      })
      .finally(() => {
        setStarting(false);
      });
  }, [isOpen, level, preferredEmailAddressId, verificationSession]);

  async function handleComplete(nextCode = code) {
    if (cancelledRef.current || !verificationSession || nextCode.trim().length !== 6 || busy) return;
    setBusy(true);
    setError(null);
    try {
      await verificationSession.attemptFirstFactorVerification({
        strategy: 'email_code',
        code: nextCode.trim(),
      });
      onComplete();
    } catch (err) {
      setError(clerkErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleResend() {
    if (!verificationSession || !factor || busy) return;
    setBusy(true);
    setError(null);
    try {
      await verificationSession.prepareFirstFactorVerification({
        strategy: 'email_code',
        emailAddressId: factor.emailAddressId,
      });
      setCode('');
    } catch (err) {
      setError(clerkErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const emailLabel = factor?.safeIdentifier ? <UnlinkedEmail address={factor.safeIdentifier} /> : 'your primary email';

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        cancelledRef.current = true;
        onCancel();
      }}
      title="Confirm it’s you"
      size="sm"
    >
      {starting && !error ? (
        <p className="text-sm text-gray-600">Sending a code to your primary email…</p>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {factor
              ? confirmationMessage(purpose, emailLabel)
              : 'Confirm this account before continuing.'}
          </p>
          {factor && (
            <VerificationCodeInput
              value={code}
              onChange={setCode}
              onComplete={(value) => void handleComplete(value)}
              error={error ?? undefined}
              disabled={busy}
            />
          )}
          {error && !factor && (
            <p className="text-sm text-error" role="alert">
              {error}
            </p>
          )}
          <div className="flex flex-wrap gap-3">
            {factor && (
              <Button
                type="button"
                variant="tertiary"
                className="min-h-[44px]"
                disabled={busy}
                onClick={() => void handleResend()}
              >
                Resend code
              </Button>
            )}
            <Button
              type="button"
              variant="tertiary"
              className="min-h-[44px]"
              disabled={busy}
              onClick={() => {
                cancelledRef.current = true;
                onCancel();
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
